import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { FaSignInAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import logoSGR from '../../assets/SGRLock.png';
import logoSwint from '../../assets/swintLogo.svg';
import Button from '../../components/Button';
import Input from '../../components/Input';
import LanguageSwitch from '../../components/LanguageSwitch';
import { useAuth } from '../../contexts/authContext';
import { useSteps } from '../../contexts/stepContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import api from '../../services/api';
import { errorToast, successToast } from '../../utils/toast';
import { Container, Content } from './styles';

interface LoginProps {
  user: string;
  password: string;
  captcha: string | null | undefined;
}

/* ---- Modal de redefinição (styled local) ---- */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
`;

const ModalTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const FieldLabel = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: #333;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
  &:focus { border-color: #ffc003; }
`;

const ErrMsg = styled.p`
  font-size: 0.8rem;
  color: #e53935;
  margin: 0;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const BtnCancel = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  color: #888;
  padding: 8px 12px;
`;

const BtnSave = styled.button`
  background: #ffc003;
  color: #1a1a2e;
  border: none;
  border-radius: 6px;
  padding: 10px 24px;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  &:hover { background: #e6ac00; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const RedefinirLink = styled.button`
  margin-top: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  color: inherit;
  text-decoration: underline;
  &:hover { opacity: 0.7; }
`;
/* ---- /modal ---- */

const Login: React.FC = () => {
  const { register, handleSubmit } = useForm<LoginProps>();
  const { signIn } = useAuth();
  const { handleSetLoading } = useSteps();
  const reCaptchaLoginRef = useRef<ReCAPTCHA>(null);
  const { translationPage } = useAppTranslation();
  const { tp } = translationPage(t => t.pages.login);
  const { tp: tpLogos } = translationPage(t => t.components.logos);

  /* Estado do modal de redefinição */
  const [showModal, setShowModal] = useState(false);
  const [cpEmail, setCpEmail] = useState('');
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpError, setCpError] = useState('');
  const [cpLoading, setCpLoading] = useState(false);

  const handleChangePassword = async () => {
    setCpError('');
    if (!cpEmail || !cpCurrent || !cpNew || !cpConfirm) {
      setCpError('Preencha todos os campos.');
      return;
    }
    if (cpNew !== cpConfirm) { setCpError('As senhas não coincidem.'); return; }
    if (cpNew.length < 8) { setCpError('A nova senha deve ter pelo menos 8 caracteres.'); return; }

    setCpLoading(true);
    try {
      const { data } = await api.post('/change-password', {
        user: cpEmail,
        current_password: cpCurrent,
        new_password: cpNew,
        confirm_password: cpConfirm,
      });
      if (data.result === 'success') {
        successToast({ message: data.message });
        setShowModal(false);
        setCpEmail(''); setCpCurrent(''); setCpNew(''); setCpConfirm('');
      } else {
        setCpError(data.message || 'Erro ao alterar a senha.');
      }
    } catch {
      setCpError('Erro ao alterar a senha. Tente novamente.');
    } finally {
      setCpLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('fpc') === '1') {
      sessionStorage.setItem('mustChangePassword', '1');
    }
  }, []);

  const onSubmit = useCallback(
    async (data: LoginProps) => {
      handleSetLoading(true);
      const captcha = await reCaptchaLoginRef.current?.executeAsync();
      reCaptchaLoginRef.current?.reset();
      if (!captcha) { errorToast({ message: tp('captchaRequired') }); return; }
      const loginData = { user: data.user, password: data.password, captcha, action: 'login' };
      const response = await signIn(loginData);
      if (response.error) errorToast({ message: response.message });
      handleSetLoading(false);
    },
    [signIn, handleSetLoading, tp],
  );

  return (
    <Container>
      <LanguageSwitch />
      <img src={logoSGR} alt={tpLogos('sgrLoc')} />
      <Content>
        <img src={logoSwint} alt={tpLogos('swint')} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Input {...register('user')} type="text" placeholder={tp('userPlaceholder')} label={tp('userLabel')} />
          <Input {...register('password')} type="password" placeholder={tp('passwordPlaceholder')} label={tp('passwordLabel')} />
          <div>
            <ReCAPTCHA sitekey={process.env.REACT_APP_RECAPTCHA_KEY_SITE || ''} size="invisible" ref={reCaptchaLoginRef} />
          </div>
          <Button type="submit" title={tp('submit')} icon={FaSignInAlt} />
        </form>
        <Link to="/forgotPassword">{tp('forgotPassword')}</Link>
        <RedefinirLink type="button" onClick={() => setShowModal(true)}>
          Redefinir senha
        </RedefinirLink>
      </Content>

      {showModal && (
        <Overlay>
          <ModalCard>
            <ModalTitle>Redefinir senha</ModalTitle>
            <ModalSubtitle>Informe seu e-mail, a senha atual e a nova senha que deseja usar.</ModalSubtitle>
            <FieldLabel>
              E-mail
              <FieldInput type="email" value={cpEmail} onChange={e => setCpEmail(e.target.value)} placeholder="Seu e-mail de acesso" autoComplete="username" />
            </FieldLabel>
            <FieldLabel>
              Senha atual
              <FieldInput type="password" value={cpCurrent} onChange={e => setCpCurrent(e.target.value)} placeholder="Senha atual" autoComplete="current-password" />
            </FieldLabel>
            <FieldLabel>
              Nova senha
              <FieldInput type="password" value={cpNew} onChange={e => setCpNew(e.target.value)} placeholder="Mínimo 8 caracteres" autoComplete="new-password" />
            </FieldLabel>
            <FieldLabel>
              Confirmar nova senha
              <FieldInput type="password" value={cpConfirm} onChange={e => setCpConfirm(e.target.value)} placeholder="Repita a nova senha" autoComplete="new-password" />
            </FieldLabel>
            {cpError && <ErrMsg>{cpError}</ErrMsg>}
            <ModalActions>
              <BtnCancel type="button" onClick={() => { setShowModal(false); setCpError(''); }}>Cancelar</BtnCancel>
              <BtnSave type="button" onClick={handleChangePassword} disabled={cpLoading}>
                {cpLoading ? 'Salvando...' : 'Salvar senha'}
              </BtnSave>
            </ModalActions>
          </ModalCard>
        </Overlay>
      )}
    </Container>
  );
};

export default Login;
